import { createContext, useContext } from 'react'

import {
  useGetEmployersEmployerInvoicesIdAmounts,
  useGetEmployersMemberInvoicesEmployerInvoiceId as useGetMemberInvoices,
} from 'utils/web_api'
import {
  type EmployersEmployerInvoice,
  type EmployersEmployerInvoiceAmounts,
  type EmployersMemberInvoiceDetail,
} from 'utils/web_api.schemas'

type InvoiceContextType = {
  invoice: EmployersEmployerInvoice
  allMemberInvoices?: EmployersMemberInvoiceDetail[]
  isLoadingAllMemberInvoices?: boolean
  amounts?: EmployersEmployerInvoiceAmounts
  isAmountsLoading: boolean
}

export const InvoiceContext = createContext<InvoiceContextType | null>(null)

export function useInvoice () {
  const context = useContext(InvoiceContext)
  if (!context) {
    throw new Error('useInvoice must be used within InvoiceProvider')
  }
  return context
}

interface InvoiceProviderProps {
  invoice: EmployersEmployerInvoice
  children: React.ReactNode
}

export function InvoiceProvider ({
  invoice,
  children,
}: InvoiceProviderProps) {
  // Load all member invoices in the background to enable downloads are quick,
  // but do not refetch unless it has been more than 5 minutes since the last fetch.
  const { data: allMemberInvoicesData, isLoading: isLoadingAllMemberInvoices, isFetching: isFetchingAllMemberInvoices } = useGetMemberInvoices(
    invoice.id,
    {
      return_all: true,
    },
    {
      query: {
        enabled: !!invoice.id,
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000,
      },
    }
  )

  const allMemberInvoices = allMemberInvoicesData?.data

  const {
    data: amountsData,
    isLoading: isAmountsLoading,
  } = useGetEmployersEmployerInvoicesIdAmounts(
    invoice.id,
    {
      query: {
        enabled: !!invoice.id,
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000,
      },
    }
  )

  return (
    <InvoiceContext.Provider
      value={{
        invoice,
        allMemberInvoices,
        isLoadingAllMemberInvoices: isLoadingAllMemberInvoices || isFetchingAllMemberInvoices,
        amounts: amountsData,
        isAmountsLoading,
      }}
    >
      {children}
    </InvoiceContext.Provider>
  )
}
